import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useOrg } from "../contexts/OrgProvider";
import { FRAMEWORKS } from "../constants/frameworks";
import { motion } from "framer-motion";
import {
  GitBranch, Search, Filter, ChevronRight, Link2, CheckCircle2
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle
} from "../components/ui/dialog";
import { PageHeader } from "../components/PageHeader";

const Mappings = () => {
  const { token } = useAuth();
  const { t, locale } = useI18n();
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFramework, setSelectedFramework] = useState("all");
  const [selectedControl, setSelectedControl] = useState(null);

  const { activeFrameworks } = useOrg();
  const frameworks = FRAMEWORKS
    .filter((f) => activeFrameworks.includes(f.id))
    .map((f) => ({ value: f.id, label: f.short, color: f.color }));

  useEffect(() => {
    fetchMappings();
  }, [token, selectedFramework, locale]);

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedFramework !== "all") params.set("framework", selectedFramework);
      if (locale) params.set("locale", locale);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const response = await axios.get(`${API}/mappings${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMappings(response.data);
    } catch (error) {
      console.error("Failed to fetch mappings:", error);
    } finally {
      setLoading(false);
    }
  };

  const getFrameworkColor = (framework) => {
    return frameworks.find(f => f.value === framework)?.color || "#6B7280";
  };

  const getFrameworkLabel = (framework) => {
    return frameworks.find(f => f.value === framework)?.label || framework;
  };

  const filteredMappings = mappings.filter(m => {
    const matchesSearch = m.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.control_number?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // Stats
  const stats = {
    total: mappings.length,
    withMappings: mappings.filter(m => m.mapping_count > 0).length,
    byFramework: frameworks.map(fw => ({
      ...fw,
      count: mappings.filter(m => m.framework === fw.value).length,
      mapped: mappings.filter(m => m.framework === fw.value && m.mapping_count > 0).length
    }))
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading control mappings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="mappings-page">
      <PageHeader
        title={t("map.title")}
        description={t("map.subtitle")}
      />

      {/* Framework Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.byFramework.map(fw => (
          <Card
            key={fw.value}
            className={`cursor-pointer transition-all ${selectedFramework === fw.value ? 'ring-2 ring-primary' : 'hover:border-primary/50'}`}
            onClick={() => setSelectedFramework(selectedFramework === fw.value ? "all" : fw.value)}
            data-testid={`mapping-framework-${fw.value}`}
          >
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: fw.color }} />
                <span className="font-medium text-sm">{fw.label}</span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-heading font-bold">{fw.count}</p>
                  <p className="text-xs text-muted-foreground">Controls</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-primary">{fw.mapped}</p>
                  <p className="text-xs text-muted-foreground">Mapped</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Summary Card */}
      <Card className="border-border bg-muted/30">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <GitBranch className="w-8 h-8 text-primary" />
              <div>
                <p className="font-semibold">Cross-Framework Coverage</p>
                <p className="text-sm text-muted-foreground">
                  {stats.withMappings} of {stats.total} controls have cross-framework mappings
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-heading font-bold">
                {stats.total > 0 ? Math.round((stats.withMappings / stats.total) * 100) : 0}%
              </p>
              <p className="text-xs text-muted-foreground">Coverage</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search controls..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="mapping-search-input"
          />
        </div>
        {selectedFramework !== "all" && (
          <Button variant="ghost" onClick={() => setSelectedFramework("all")}>
            Clear Filter
          </Button>
        )}
      </div>

      {/* Mappings List */}
      <ScrollArea className="h-[calc(100vh-32rem)]">
        <div className="space-y-2 pr-4">
          {filteredMappings.map((control) => (
            <motion.div
              key={control.control_id}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card
                className={`border-border cursor-pointer transition-all hover:border-primary/50 ${control.mapping_count > 0 ? '' : 'opacity-60'}`}
                onClick={() => setSelectedControl(control)}
                data-testid={`mapping-card-${control.control_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: getFrameworkColor(control.framework) }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs">
                            {getFrameworkLabel(control.framework)}
                          </Badge>
                          <span className="font-mono text-xs text-muted-foreground">
                            {control.control_number}
                          </span>
                        </div>
                        <p className="text-sm font-medium truncate">{control.title}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      {control.mapping_count > 0 ? (
                        <Badge variant="secondary" className="bg-primary/10 text-primary">
                          <Link2 className="w-3 h-3 mr-1" />
                          {control.mapping_count} mapped
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground">
                          No mappings
                        </Badge>
                      )}
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>

                  {/* Preview of mapped controls */}
                  {control.mapped_controls?.length > 0 && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                      <span className="text-xs text-muted-foreground">Maps to:</span>
                      <div className="flex flex-wrap gap-1">
                        {control.mapped_controls.slice(0, 3).map((mapped, idx) => (
                          <Badge
                            key={idx}
                            variant="outline"
                            className="text-xs"
                            style={{ borderColor: getFrameworkColor(mapped.framework) }}
                          >
                            {mapped.control_number}
                          </Badge>
                        ))}
                        {control.mapped_controls.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{control.mapped_controls.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}

          {filteredMappings.length === 0 && (
            <div className="text-center py-12">
              <GitBranch className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No controls found</p>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Control Detail Dialog */}
      <Dialog open={!!selectedControl} onOpenChange={() => setSelectedControl(null)}>
        <DialogContent className="max-w-2xl">
          {selectedControl && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: getFrameworkColor(selectedControl.framework) }}
                  />
                  <Badge variant="outline">
                    {getFrameworkLabel(selectedControl.framework)}
                  </Badge>
                  <span className="font-mono text-sm text-muted-foreground">
                    {selectedControl.control_number}
                  </span>
                </div>
                <DialogTitle className="text-xl">{selectedControl.title}</DialogTitle>
                <DialogDescription>
                  {selectedControl.category && `Category: ${selectedControl.category}`}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Mapped Controls */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Link2 className="w-4 h-4" />
                    Cross-Framework Mappings
                  </h4>
                  {selectedControl.mapped_controls?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedControl.mapped_controls.map((mapped, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
                        >
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: getFrameworkColor(mapped.framework) }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs">
                                {getFrameworkLabel(mapped.framework)}
                              </Badge>
                              <span className="font-mono text-xs">
                                {mapped.control_number}
                              </span>
                            </div>
                            <p className="text-sm">{mapped.title}</p>
                          </div>
                          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 rounded-lg bg-muted/30">
                      <p className="text-muted-foreground">
                        No cross-framework mappings available for this control
                      </p>
                    </div>
                  )}
                </div>

                {/* Implementation Tip */}
                <Card className="border-primary/20 bg-primary/5">
                  <CardContent className="pt-4">
                    <p className="text-sm">
                      <strong>Tip:</strong> Implementing this control can help satisfy requirements
                      across {(selectedControl.mapped_controls?.length || 0) + 1} frameworks simultaneously.
                      This "do once, satisfy many" approach accelerates compliance efforts.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Mappings;
